import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { ReactNode, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export const Tooltip = ({
  message,
  size = 'xs',
  placement = undefined,
  position = undefined,
  align = 'center',
  children = null,
}: {
  message: ReactNode
  size?: 'xs' | 'sm' | 'md' | 'lg'
  placement?: 'right' | 'left'
  position?: 'top' | 'bottom'
  align?: 'center' | 'start'
  children?: ReactNode
}): JSX.Element => {
  const [show, setShow] = useState(false)
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const isStringMessage = typeof message === 'string'
  if (message === null || message === undefined || message === false || message === '') return <>{children}</>

  const handleMouseEnter = () => {
    if (triggerRef.current) setTriggerRect(triggerRef.current.getBoundingClientRect())
    setShow(true)
  }

  const getPortalStyle = (): React.CSSProperties => {
    if (!triggerRect) return {}

    const isBottom = position === 'bottom'
    const GAP = 10

    const style: React.CSSProperties = { position: 'fixed', zIndex: 9999 }

    // Vertical: top tooltips grow upward (anchor to trigger top), bottom tooltips grow downward
    if (isBottom) {
      style.top = triggerRect.bottom + GAP
    } else {
      style.bottom = window.innerHeight - triggerRect.top + GAP
    }

    // Horizontal: center, left-edge, or right-edge of trigger
    if (!placement) {
      style.left = triggerRect.left + triggerRect.width / 2
      style.transform = 'translateX(-50%)'
    } else if (placement === 'right') {
      style.right = window.innerWidth - triggerRect.right
    } else {
      style.left = triggerRect.left
    }

    return style
  }

  const arrowAlign = !placement ? 'self-center' : placement === 'right' ? 'self-end mr-1' : 'self-start ml-1'

  const panel =
    show &&
    triggerRect &&
    createPortal(
      <div style={getPortalStyle()} className="flex flex-col font-variation-settings-500 pointer-events-none">
        {position === 'bottom' && <div className={`w-3 h-3 -mb-2 rotate-45 bg-gray-900 ${arrowAlign}`} />}
        <div
          className={`p-2 leading-normal text-white bg-gray-900 shadow-lg rounded-md ${
            isStringMessage ? 'whitespace-pre-wrap max-w-sm' : 'whitespace-normal'
          } ${size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-sm' : size === 'md' ? 'text-md' : 'text-lg'}`}
        >
          {isStringMessage ? <span dangerouslySetInnerHTML={{ __html: message.replace(/\n/g, '<br />') }} /> : message}
        </div>
        {position !== 'bottom' && <div className={`w-3 h-3 -mt-2 rotate-45 bg-gray-900 ${arrowAlign}`} />}
      </div>,
      document.body,
    )

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative flex flex-col ${align === 'start' ? 'items-start' : 'items-center'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
      >
        {children || (
          <span className="cursor-pointer text-gray-500">
            <QuestionMarkCircleIcon className="w-[14px] h-[14px]" />
          </span>
        )}
      </div>
      {panel}
    </>
  )
}
